import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { AuthService, User } from '../../services/auth.service';
import { LoginModalComponent } from '../login-modal/login-modal.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    LoginModalComponent
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  animations: [
    trigger('slideDown', [
      state('in', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'translateY(-100%)' }),
        animate(300)
      ]),
      transition('* => void', [
        animate(300, style({ opacity: 0, transform: 'translateY(-100%)' }))
      ])
    ])
  ]
})
export class HeaderComponent implements OnInit {
  isScrolled = false;
  isMobileMenuOpen = false;
  isSearchOpen = false;
  searchQuery = '';
  showLoginModal = false;
  currentUser: User | null = null;
  isAdmin = false;
  isAuthenticated = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    // Subscribe to current user
    this.authService.currentUser.subscribe(user => {
      this.currentUser = user;
    });

    // Only access localStorage in browser environment
    if (isPlatformBrowser(this.platformId)) {
      try {
        // Check if the admin token exists in localStorage
        const adminToken = localStorage.getItem('adminToken');
        const username = localStorage.getItem('username');
        
        this.isAdmin = !!adminToken || username === 'admin';
        this.isAuthenticated = !!adminToken || !!username;
        
        // Setup scroll listener only in browser
        this.setupScrollListener();
      } catch (error) {
        // Handle localStorage errors gracefully
        this.isAdmin = false;
        this.isAuthenticated = false;
      }
    }
  }

  private setupScrollListener(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('scroll', () => {
        this.isScrolled = window.scrollY > 20;
      });
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  toggleSearch(): void {
    this.isSearchOpen = !this.isSearchOpen;
    if (!this.isSearchOpen) {
      this.searchQuery = '';
    }
  }

  onSearchSubmit(): void {
    if (this.searchQuery.trim()) {
      this.router.navigate(['/products'], {
        queryParams: { search: this.searchQuery.trim() }
      });
      this.toggleSearch();
    }
  }

  onSearchInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearchSubmit();
    } else if (event.key === 'Escape') {
      this.toggleSearch();
    }
  }

  // Auth related methods
  openLoginModal(): void {
    this.showLoginModal = true;
  }

  onLoginSuccess(): void {
    this.showLoginModal = false;
    
    // Only access localStorage in browser environment
    if (isPlatformBrowser(this.platformId)) {
      try {
        // Check if admin token exists
        const adminToken = localStorage.getItem('adminToken');
        const username = localStorage.getItem('username');
        
        if (adminToken || username === 'admin') {
          this.isAdmin = true;
          this.isAuthenticated = true;
          
          // Force navigation to admin dashboard
          this.router.navigate(['/admin/dashboard']).then(() => {
            // Navigation successful
          });
        }
      } catch (error) {
        // Handle localStorage errors
      }
    }
  }

  closeLoginModal(): void {
    this.showLoginModal = false;
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('username');
        localStorage.removeItem('id');
        localStorage.removeItem('currentUser');
      } catch (error) {
        // Handle localStorage errors
      }
    }
    
    this.isAdmin = false;
    this.isAuthenticated = false;
    this.authService.logout();
    this.router.navigate(['/']);
  }

  navigateToAdminDashboard(): void {
    this.router.navigate(['/admin/dashboard']);
  }

  navigateToCart(): void {
    // Cart functionality
  }

  navigateToWishlist(): void {
    // Wishlist functionality
  }

  navigateToProfile(): void {
    // Profile functionality
  }

  get isAdminUser(): boolean {
    return this.authService.isAdmin();
  }

  get isUserAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  // Safe localStorage access methods
  private getFromLocalStorage(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  private setToLocalStorage(key: string, value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        // Handle error silently
      }
    }
  }

  private removeFromLocalStorage(key: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        // Handle error silently
      }
    }
  }
}
